import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createAdmin,
  updateAdmin,
  getAdmin,
  uploadImage,
  uploadPdf,
  formatApiError,
} from "@/lib/adminApi";
import { Loader2, ArrowRight, Upload, X } from "lucide-react";

const PUBLICATION_TYPES = [
  { v: "article", l: "مقالة" },
  { v: "release", l: "إصدار" },
  { v: "case-study", l: "دراسة حالة" },
  { v: "research", l: "بحث" },
];

const PATH_SLUGS = [
  { v: "community-participation", l: "المشاركة المجتمعية" },
  { v: "social-innovation", l: "الابتكار الاجتماعي" },
  { v: "social-investment", l: "الاستثمار الاجتماعي" },
  { v: "guests-of-rahman", l: "ضيوف الرحمن" },
  { v: "social-entrepreneurship", l: "الريادة الاجتماعية" },
  { v: "community-development", l: "تنمية المجتمعات" },
  { v: "social-responsibility", l: "المسؤولية الاجتماعية" },
];

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\u0600-\u06FF]+/gu, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 140);
}

export default function AdminForm({ entity }) {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    slug: "",
    title: "",
    excerpt: "",
    body: "",
    type: entity === "publications" ? "article" : null,
    status: "draft",
    published_at: "",
    cover_image: "",
    pdf_url: "",
    pdf_filename: "",
    path_slug: entity === "projects" ? "community-development" : null,
    meta_description: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [imgUp, setImgUp] = useState(false);
  const [pdfUp, setPdfUp] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    getAdmin(entity, id)
      .then((data) => {
        setForm({
          slug: data.slug || "",
          title: data.title || "",
          excerpt: data.excerpt || "",
          body: data.body || "",
          type: data.type || (entity === "publications" ? "article" : null),
          status: data.status || "draft",
          published_at: data.published_at ? data.published_at.slice(0, 16) : "",
          cover_image: data.cover_image || "",
          pdf_url: data.pdf_url || "",
          pdf_filename: data.pdf_filename || "",
          path_slug: data.path_slug || (entity === "projects" ? "community-development" : null),
          meta_description: data.meta_description || "",
        });
        setSlugTouched(true);
      })
      .catch(() => setErr("تعذّر جلب العنصر."))
      .finally(() => setLoading(false));
  }, [entity, id, isEdit]);

  const update = (patch) =>
    setForm((f) => {
      const next = { ...f, ...patch };
      if (patch.title != null && !slugTouched && !isEdit) {
        next.slug = slugify(patch.title);
      }
      return next;
    });

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    setImgUp(true);
    try {
      const r = await uploadImage(f);
      update({ cover_image: r.url });
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail));
    } finally {
      setImgUp(false);
    }
  };

  const onPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    setPdfUp(true);
    try {
      const r = await uploadPdf(f);
      update({ pdf_url: r.url, pdf_filename: r.filename });
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail));
    } finally {
      setPdfUp(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || slugify(form.title),
        published_at: form.published_at
          ? new Date(form.published_at).toISOString()
          : null,
      };
      if (entity !== "publications") payload.type = null;
      if (entity !== "projects") payload.path_slug = null;
      if (isEdit) {
        await updateAdmin(entity, id, payload);
      } else {
        await createAdmin(entity, payload);
      }
      navigate(`/admin/${entity}`);
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="animate-spin text-blue" />
      </div>
    );
  }

  const field = "w-full bg-white border border-navy/20 px-3 py-2.5 focus-visible:ring-2 focus-visible:ring-coral outline-none";

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-6" data-testid="admin-form">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/admin/${entity}`)}
          className="text-blue/70 hover:text-navy inline-flex items-center gap-1 text-sm"
        >
          <ArrowRight size={14} /> العودة
        </button>
      </div>
      <h1 className="font-display text-2xl">{isEdit ? "تعديل" : "إضافة"}</h1>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="block text-sm mb-1 text-blue/80">العنوان <span className="text-coral">*</span></span>
          <input
            className={field}
            required
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            data-testid="form-title"
          />
        </label>

        <label className="block">
          <span className="block text-sm mb-1 text-blue/80">Slug (رابط الصفحة)</span>
          <input
            dir="ltr"
            className={`${field} text-left`}
            value={form.slug}
            onChange={(e) => {
              setSlugTouched(true);
              update({ slug: slugify(e.target.value) });
            }}
            data-testid="form-slug"
          />
        </label>

        <label className="block">
          <span className="block text-sm mb-1 text-blue/80">تاريخ النشر</span>
          <input
            type="datetime-local"
            className={field}
            value={form.published_at}
            onChange={(e) => update({ published_at: e.target.value })}
          />
        </label>

        {entity === "publications" && (
          <label className="block">
            <span className="block text-sm mb-1 text-blue/80">النوع</span>
            <select
              className={field}
              value={form.type || "article"}
              onChange={(e) => update({ type: e.target.value })}
              data-testid="form-type"
            >
              {PUBLICATION_TYPES.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </label>
        )}

        {entity === "projects" && (
          <label className="block">
            <span className="block text-sm mb-1 text-blue/80">مسار التأثير</span>
            <select
              className={field}
              value={form.path_slug || ""}
              onChange={(e) => update({ path_slug: e.target.value })}
            >
              {PATH_SLUGS.map((o) => (
                <option key={o.v} value={o.v}>{o.l}</option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="block text-sm mb-1 text-blue/80">الحالة</span>
          <select
            className={field}
            value={form.status}
            onChange={(e) => update({ status: e.target.value })}
            data-testid="form-status"
          >
            <option value="published">منشور</option>
            <option value="draft">مسودة</option>
          </select>
        </label>
      </div>

      <label className="block">
        <span className="block text-sm mb-1 text-blue/80">مقتطف</span>
        <textarea
          rows={2}
          maxLength={600}
          className={field}
          value={form.excerpt}
          onChange={(e) => update({ excerpt: e.target.value })}
          data-testid="form-excerpt"
        />
      </label>

      <label className="block">
        <span className="block text-sm mb-1 text-blue/80">المحتوى</span>
        <textarea
          rows={12}
          className={field}
          value={form.body}
          onChange={(e) => update({ body: e.target.value })}
          data-testid="form-body"
          placeholder="اكتب المحتوى هنا. يمكن استخدام أسطر جديدة، والروابط ستظهر كنص عادي."
        />
      </label>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <span className="block text-sm mb-2 text-blue/80">صورة الغلاف (JPG/PNG/WEBP — حد 3MB)</span>
          {form.cover_image ? (
            <div className="relative inline-block">
              <img src={form.cover_image} alt="غلاف" className="max-h-40 border border-navy/10" />
              <button
                type="button"
                onClick={() => update({ cover_image: "" })}
                className="absolute -top-2 -right-2 bg-white border border-navy/20 rounded-full p-1"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 border border-dashed border-navy/30 px-4 py-3 cursor-pointer hover:border-coral">
              {imgUp ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              <span className="text-sm">اختر صورة</span>
              <input type="file" accept="image/*" onChange={onImage} className="hidden" />
            </label>
          )}
        </div>

        <div>
          <span className="block text-sm mb-2 text-blue/80">مرفق PDF (اختياري — حد 10MB)</span>
          {form.pdf_url ? (
            <div className="flex items-center gap-2 border border-navy/10 bg-white px-3 py-2">
              <a href={form.pdf_url} target="_blank" rel="noreferrer" className="text-navy hover:text-coral text-sm truncate max-w-[240px]">
                {form.pdf_filename || "الملف مرفق"}
              </a>
              <button
                type="button"
                onClick={() => update({ pdf_url: "", pdf_filename: "" })}
                className="text-coral"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className="inline-flex items-center gap-2 border border-dashed border-navy/30 px-4 py-3 cursor-pointer hover:border-coral">
              {pdfUp ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              <span className="text-sm">اختر PDF</span>
              <input type="file" accept="application/pdf" onChange={onPdf} className="hidden" />
            </label>
          )}
        </div>
      </div>

      <label className="block">
        <span className="block text-sm mb-1 text-blue/80">Meta description (لـ SEO — 150-160 حرف)</span>
        <input
          maxLength={300}
          className={field}
          value={form.meta_description}
          onChange={(e) => update({ meta_description: e.target.value })}
        />
      </label>

      {err && (
        <div className="border border-coral/60 text-coral px-3 py-2 text-sm" role="alert">
          {err}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-save"
          className="bg-coral text-white px-6 py-3 font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : null}
          {isEdit ? "حفظ التغييرات" : "إضافة"}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/admin/${entity}`)}
          className="text-navy/70 hover:text-navy px-4 py-3"
        >
          إلغاء
        </button>
      </div>
    </form>
  );
}
